/**
 * Match Hub screen — hero, tabs, lazy-feeling panels (all mock data in v1).
 */

import React, { useEffect, useMemo, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { MatchHubHero } from '../MatchHubHero';
import { MatchHubTabBar } from '../MatchHubTabBar';
import { MatchHubOverviewPanel } from '../MatchHubOverview';
import { MatchHubLineupsPanel } from '../MatchHubLineups';
import { MatchHubTablePanel } from '../MatchHubTable';
import { MatchHubH2HPanel } from '../MatchHubH2H';
import { useTheme } from '../../../src/theme/useTheme';
import type { MatchHubMockData, MatchHubTabKey } from '../../../src/match/types/matchHub.types';
import { getMatchHubStyles } from './MatchHub.styles';

export interface MatchHubProps {
  data: MatchHubMockData;
  onBack: () => void;
}

export const MatchHub: React.FC<MatchHubProps> = ({ data, onBack }) => {
  const theme = useTheme();
  const styles = useMemo(() => getMatchHubStyles(theme), [theme]);
  const [tab, setTab] = useState<MatchHubTabKey>('overview');
  const hasTable = data.standings.length > 0;
  const hiddenTabs: readonly MatchHubTabKey[] = hasTable ? [] : ['table'];

  useEffect(() => {
    if (!hasTable && tab === 'table') setTab('overview');
  }, [hasTable, tab]);

  return (
    <View style={styles.bodyBg} testID="match-hub-screen">
      <ScrollView
        style={styles.flex}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.content}>
          <MatchHubHero data={data} onBack={onBack} />
          <MatchHubTabBar active={tab} onChange={setTab} hiddenTabs={hiddenTabs} />
          {tab === 'overview' && (
            <MatchHubOverviewPanel
              data={data}
              onOpenTableTab={() => {
                setTab('table');
              }}
            />
          )}
          {tab === 'lineups' && <MatchHubLineupsPanel data={data} />}
          {tab === 'table' && <MatchHubTablePanel data={data} />}
          {tab === 'h2h' && <MatchHubH2HPanel data={data} />}
        </View>
      </ScrollView>
    </View>
  );
};
